import { Button, Tag, TagCloseButton, TagLabel, Wrap, WrapItem, WrapProps } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useHubsLibraryFilters } from '@/components/ui/features/hubs-conference/LibraryFilter/HubsLibraryFilter.hooks';
import { generateFilterChips } from '@/components/ui/shared/tags/HubTagFilters/HubTagFilters.utils';
import { LibraryFilter } from '@/enums/LibraryFilter';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { queryAllLecturePackages } from '@/queries/lecture-packages';
import { queryAllLectureTags } from '@/queries/lecture-tags';
import { queryLibrarySubject } from '@/queries/library-subjects';
import { queryThinConference } from '@/queries/thin-conferences';
import { queryThinEvent } from '@/queries/thin-events';

export const HubTagFilters: FC<WrapProps> = ({ ...rest }) => {
  const { t } = useTranslation();

  const { change, filters: filtersObj, reset } = useHubsLibraryFilters();

  const { data: selectedSubject } = useResource(queryLibrarySubject(filtersObj[LibraryFilter.Subject]), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: selectedConference } = useResource(queryThinConference(filtersObj[LibraryFilter.Publisher]), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: selectedEvent } = useResource(queryThinEvent(filtersObj[LibraryFilter.Event]), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: allTagsData } = useResources(queryAllLectureTags(), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: allLecturePackages } = useResources(queryAllLecturePackages(), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const filters = useMemo(
    () =>
      generateFilterChips(
        filtersObj,
        selectedConference,
        selectedSubject,
        selectedEvent,
        allTagsData,
        allLecturePackages
      ),
    [filtersObj, selectedConference, selectedSubject, selectedEvent, allTagsData, allLecturePackages]
  );

  const onTagRemove = (item) => {
    const { key, value } = item;

    if (key === LibraryFilter.Publisher) {
      change({ [key]: null }, { shallow: true, method: 'replace' });
      return;
    }

    if (key === LibraryFilter.Subject) {
      change({ [key]: null }, { shallow: true, method: 'replace' });
      return;
    }

    const allFilters = filtersObj[key].split(',');
    const updatedFilter = allFilters.filter((f) => f !== value).join(',');

    change({ [key]: updatedFilter || null }, { shallow: true, method: 'replace' });
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <Wrap {...rest}>
      {filters.map((item) => {
        const { key, value, label } = item;

        return (
          <WrapItem key={`${key}-${value}`}>
            <Tag h="28px" px={3} bgColor="violet.50" color="violet.500" borderRadius="full">
              <TagLabel maxW="200px" fontWeight="medium">
                {label}
              </TagLabel>
              <TagCloseButton onClick={() => onTagRemove(item)} />
            </Tag>
          </WrapItem>
        );
      })}
      <WrapItem>
        <Button variant="unstyled" alignSelf="center" onClick={reset} color="gray.500" h="max" fontWeight="normal">
          {t('library.clearAll')}
        </Button>
      </WrapItem>
    </Wrap>
  );
};
